type OrderedEntity = { id: string; order?: number | null };

export const locationsTreeBuilder = <
  TGroup extends OrderedEntity & { parentId?: string | null; name: string },
  TLocation extends OrderedEntity & { groupId?: string | null; details: { name: string } }
>({
  groups,
  locations,
  controlledOrders,
}: {
  groups: TGroup[];
  locations: TLocation[];
  controlledOrders?: Record<string, number>;
}) => {
  const orderedGroups = [...groups].sort((a, b) => {
    const aOrder = controlledOrders?.[a.id] ?? a.order ?? 100;
    const bOrder = controlledOrders?.[b.id] ?? b.order ?? 100;
    if (aOrder === bOrder) {
      return a.name.localeCompare(b.name, undefined, { numeric: true });
    }
    return aOrder - bOrder;
  });

  const orderedLocations = [...locations].sort((a, b) => {
    const aOrder = controlledOrders?.[a.id] ?? a.order ?? 100;
    const bOrder = controlledOrders?.[b.id] ?? b.order ?? 100;
    if (aOrder === bOrder) {
      return a.details.name.localeCompare(b.details.name, undefined, { numeric: true });
    }
    return aOrder - bOrder;
  });

  const groupMap = new Map<string, TGroup & { childGroups: string[]; childLocations: string[] }>();
  const locationMap = new Map<string, TLocation>();

  groups.reduce((acc, group) => {
    acc.set(group.id, {
      ...group,
      childGroups: [],
      childLocations: [],
    });
    return acc;
  }, groupMap);

  orderedLocations.forEach((location) => {
    locationMap.set(location.id, location);
    if (location.groupId) {
      groupMap.get(location.groupId)?.childLocations.push(location.id);
    }
  });

  orderedGroups.forEach((group) => {
    if (group.parentId) {
      groupMap.get(group.parentId)?.childGroups.push(group.id);
    }
  });

  return {
    orderedGroups,
    orderedLocations,
    groupMap,
    locationMap,
  };
};
